import { FC } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Avatar,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiUsers, FiHome, FiFileText, FiList } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { SearchResult } from './global-search';

interface AnalystResultsProps {
  results: SearchResult[];
  onResultClick: (result: SearchResult) => void;
  selectedIndex: number;
}

export const AnalystResults: FC<AnalystResultsProps> = ({ results, onResultClick, selectedIndex }) => {
  const { t } = useTranslation();
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  // Analyst-specific mock data
  const analystMockResults: SearchResult[] = [
    // Assessments
    {
      id: 'analyst-1',
      type: 'assessment',
      title: 'Avaliação de Competências - João Silva',
      subtitle: 'Status: Em andamento',
      description: 'Avaliação de competências técnicas e comportamentais',
      href: '/avaliacoes/1',
      metadata: { status: 'in_progress', progress: 75, questions: 50 }
    },
    {
      id: 'analyst-2',
      type: 'assessment',
      title: 'Teste de Personalidade - Maria Santos',
      subtitle: 'Status: Concluído',
      description: 'Avaliação de perfil comportamental DISC',
      href: '/avaliacoes/2',
      metadata: { status: 'completed', score: 85, date: '2024-03-10' }
    },
    // People
    {
      id: 'analyst-3',
      type: 'person',
      title: 'João Silva',
      subtitle: 'Desenvolvedor Full Stack',
      description: 'joao.silva@techcorp.com',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
      href: '/pessoas/3',
      metadata: { department: 'TI', status: 'active', assessments: 2 }
    },
    {
      id: 'analyst-4',
      type: 'person',
      title: 'Maria Santos',
      subtitle: 'Product Manager',
      description: 'maria.santos@techcorp.com',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
      href: '/pessoas/4',
      metadata: { department: 'Product', status: 'active', assessments: 1 }
    },
    // Companies
    {
      id: 'analyst-5',
      type: 'company',
      title: 'TechCorp Solutions',
      subtitle: 'Tecnologia',
      description: 'Empresa de desenvolvimento de software',
      href: '/empresas/5',
      metadata: { employees: 150, industry: 'Technology', assessments: 25 }
    },
    {
      id: 'analyst-6',
      type: 'company',
      title: 'Inovação Digital Ltda',
      subtitle: 'Consultoria',
      description: 'Consultoria em transformação digital',
      href: '/empresas/6',
      metadata: { employees: 45, industry: 'Consulting', assessments: 12 }
    },
    // Groups
    {
      id: 'analyst-7',
      type: 'group',
      title: 'Equipe de Desenvolvimento',
      subtitle: '15 membros',
      description: 'Grupo de desenvolvedores da TechCorp',
      href: '/listas/7',
      metadata: { members: 15, type: 'development', assessments: 8 }
    },
    {
      id: 'analyst-8',
      type: 'group',
      title: 'Líderes de Projeto',
      subtitle: '8 membros',
      description: 'Grupo de líderes e gerentes de projeto',
      href: '/listas/8',
      metadata: { members: 8, type: 'leadership', assessments: 5 }
    }
  ];

  const getTypeIcon = (type: SearchResult['type']) => {
    switch (type) {
      case 'assessment':
        return <FiFileText />;
      case 'person':
        return <FiUsers />;
      case 'company':
        return <FiHome />;
      case 'group':
        return <FiList />;
      default:
        return <FiUsers />;
    }
  };

  const getTypeColor = (type: SearchResult['type']) => {
    switch (type) {
      case 'assessment':
        return 'blue';
      case 'person':
        return 'green';
      case 'company':
        return 'purple';
      case 'group':
        return 'orange';
      default:
        return 'gray';
    }
  };

  const getTypeLabel = (type: SearchResult['type']) => {
    switch (type) {
      case 'assessment':
        return t('search.assessment');
      case 'person':
        return t('search.person');
      case 'company':
        return t('search.company');
      case 'group':
        return t('search.group');
      default:
        return type;
    }
  };

  // Combine mock results with actual results for analyst
  const allResults = [...analystMockResults, ...results];

  if (allResults.length === 0) {
    return (
      <Box py={8} textAlign="center">
        <Text color="gray.500">{t('search.no_results')}</Text>
      </Box>
    );
  }

  return (
    <VStack spacing={0} align="stretch">
      {allResults.map((result, index) => {
        const isSelected = index === selectedIndex;
        return (
          <Box
            key={result.id}
            px={4}
            py={3}
            cursor="pointer"
            bg={isSelected ? hoverBg : 'transparent'}
            _hover={{ bg: hoverBg }}
            onClick={() => onResultClick(result)}
            borderBottom={index < allResults.length - 1 ? '1px solid' : 'none'}
            borderColor={useColorModeValue('gray.100', 'gray.600')}
            borderLeft={isSelected ? '3px solid' : '3px solid transparent'}
            borderLeftColor="blue.500"
          >
            <HStack spacing={3} align="start">
              {result.avatar ? (
                <Avatar size="sm" src={result.avatar} name={result.title} />
              ) : (
                <Box
                  p={2}
                  borderRadius="md"
                  bg={`${getTypeColor(result.type)}.100`}
                  color={`${getTypeColor(result.type)}.600`}
                >
                  {getTypeIcon(result.type)}
                </Box>
              )}
              <Box flex={1}>
                <Text fontWeight="medium" fontSize="sm" noOfLines={1}>
                  {result.title}
                </Text>
                {result.subtitle && (
                  <Text fontSize="xs" color="gray.500" noOfLines={1}>
                    {result.subtitle}
                  </Text>
                )}
                {result.description && (
                  <Text fontSize="xs" color="gray.400" noOfLines={1}>
                    {result.description}
                  </Text>
                )}
              </Box>
              <Badge size="sm" colorScheme={getTypeColor(result.type)} variant="subtle">
                {getTypeLabel(result.type)}
              </Badge>
            </HStack>
          </Box>
        );
      })}
    </VStack>
  );
}; 